import { useEffect } from 'react'
import { Alert, Button, Col, Modal, Row, Spinner } from 'react-bootstrap'
import moment from 'moment'
import { HardwareCheckGroupCard } from './HardwareCheckGroupCard'

const STATUS_POLL_MS = 3000

/**
 * Hardware Check popup: a card per setup group (camera + weighbridge), Recheck, and the one
 * fix operators can make — swapping two groups' weighbridge ports when the cables got
 * swapped. `hc` is the state from useHardwareCheck().
 */
export function HardwareCheckModal({ show, onHide, hc }) {
    const { analysis, result, liveReadActive, busyMessage, running, swapping, refreshStatus, run, swap } = hc
    const groups = analysis.groups || []
    const suggestion = analysis.suggestion
    const busy = liveReadActive || !!busyMessage
    const disabled = busy || running || swapping
    const suggestedIds = new Set(((suggestion && suggestion.pairs) || []).flat())
    const nameOf = (id) => (groups.find((g) => g.groupId === id) || {}).name

    // Refresh on open; while a live reading blocks the check, poll until it is free.
    useEffect(() => {
        if (show) refreshStatus()
    }, [show, refreshStatus])
    useEffect(() => {
        if (!show || !busy) return undefined
        const t = setInterval(refreshStatus, STATUS_POLL_MS)
        return () => clearInterval(t)
    }, [show, busy, refreshStatus])

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-cpu text-primary me-2" />
                    Hardware Check
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {busy && (
                    <Alert variant="warning">
                        <i className="bi bi-hourglass-split me-2" />
                        {busyMessage || 'A weight reading is in progress.'} The check will be available again once it finishes.
                    </Alert>
                )}

                {suggestion && suggestion.type === 'SWAP' && !running && (
                    <Alert variant="danger" className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <span>
                            <i className="bi bi-arrow-left-right me-2" />
                            <strong>Ports look swapped — swap now?</strong>
                            <span className="d-block fs-7 mt-1">
                                {suggestion.moves.map((m) => `${m.name}'s weighbridge is on ${m.onPortOf}'s cable`).join('; ')}.
                            </span>
                        </span>
                        {suggestion.pairs.map(([a, b]) => (
                            <Button key={a + b} variant="danger" size="sm" disabled={disabled} onClick={() => swap(a, b)}>
                                Swap {nameOf(a)} ⇄ {nameOf(b)}
                            </Button>
                        ))}
                    </Alert>
                )}
                {suggestion && suggestion.type === 'MAPPING' && !running && (
                    <Alert variant="danger">
                        <strong>Weighbridge cables look mixed up.</strong>
                        <ul className="mb-0 mt-2 fs-7">
                            {suggestion.moves.map((m) => (
                                <li key={m.groupId}>{m.name}'s weighbridge is on {m.onPortOf}'s cable</li>
                            ))}
                        </ul>
                        <span className="fs-7">Use "Swap weighbridge with…" on the cards to fix them one pair at a time.</span>
                    </Alert>
                )}

                {running ? (
                    <div className="text-center py-10">
                        <Spinner animation="border" variant="primary" className="mb-3" />
                        <div className="text-muted">Checking cameras and weighbridges… this takes a few seconds.</div>
                    </div>
                ) : !result ? (
                    <div className="text-center py-10 text-muted">
                        <i className="bi bi-question-circle fs-2x d-block mb-3" />
                        Not checked yet. Press <strong>Check now</strong> to test the cameras and weighbridges.
                    </div>
                ) : groups.length === 0 ? (
                    <div className="text-center py-10 text-muted">No weighing setups are enabled on this outpost.</div>
                ) : (
                    <Row className="g-4">
                        {groups.map((g, i) => (
                            <Col key={g.groupId} md={groups.length === 1 ? 12 : 6}>
                                <HardwareCheckGroupCard group={g} index={i} disabled={disabled}
                                    highlight={suggestedIds.has(g.groupId)}
                                    others={groups.length > 2 ? groups.filter((o) => o.groupId !== g.groupId) : []}
                                    onSwapWith={(otherId) => swap(g.groupId, otherId)} />
                            </Col>
                        ))}
                    </Row>
                )}

                {groups.length > 1 && (
                    <div className="text-muted fs-7 mt-5">
                        <i className="bi bi-lightbulb me-1" />
                        If both weighbridges show the same format, place a vehicle on one weighbridge and press
                        Recheck to see which card shows the weight.
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <span className="text-muted fs-8">
                    {result && result.checkedAt ? `Last checked ${moment(result.checkedAt).format('hh:mm:ss A')}` : 'Not checked'}
                </span>
                <div className="d-flex gap-2">
                    {groups.length === 2 && (
                        <Button variant={suggestion ? 'danger' : 'light-primary'} disabled={disabled}
                            onClick={() => swap(groups[0].groupId, groups[1].groupId)}>
                            {swapping ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-arrow-left-right me-2" />}
                            Swap weighbridge ports
                        </Button>
                    )}
                    <Button variant="primary" disabled={disabled} onClick={run}>
                        {running ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-arrow-repeat me-2" />}
                        {result ? 'Recheck' : 'Check now'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}
